import { PostApiSaleBodyDataItemsItem } from '@/shared/api/models';

type SaleDiscountRow = {
    discountName?: string | null;
    discountAmount?: number | string | null;
    discountSource?: string | null;
};

type SaleWithDiscounts = {
    totalDiscount?: number | null;
    salesDiscounts?: SaleDiscountRow[] | null;
    items?: { discounts?: SaleDiscountRow[] | null }[] | null;
};

const sumBySource = (rows: SaleDiscountRow[], source: string) =>
    rows
        .filter((row) => row?.discountSource === source)
        .reduce((sum, row) => sum + (Number(row?.discountAmount) || 0), 0);

// salesDiscounts mirrors the item-level rows, so read one or the other — never both.
const getSaleDiscountRows = (salesData: SaleWithDiscounts | null | undefined): SaleDiscountRow[] => {
    const itemRows = (salesData?.items || []).flatMap((item) => item?.discounts || []);
    return itemRows.some((row) => row?.discountSource) ? itemRows : salesData?.salesDiscounts || [];
};

/**
 * What the sale discounted on top of the booking's own discount, split by where it came from so
 * each kind can be listed on its own line: a settlement of a deposit shortfall, and a regular
 * discount given at the till. The booking's share is excluded — callers show that separately.
 *
 * Amounts come from the sale's recorded totals rather than from a gap between the booking total
 * and the amount paid, since a booking discount produces that same gap.
 *
 * bookingDiscountTotal is only consulted for sales whose rows predate discountSource; tagged rows
 * state the booking's share themselves.
 */
export const getSaleNonBookingDiscounts = (
    salesData: SaleWithDiscounts | null | undefined,
    bookingDiscountTotal: number = 0,
): Array<{ source: 'SETTLEMENT' | null; amount: number }> => {
    if (!salesData) return [];

    const rows = getSaleDiscountRows(salesData);
    const taggedBookingAmount = sumBySource(rows, 'BOOKING');
    const recordedTotal = Number(salesData.totalDiscount) || 0;
    const discountTotal =
        recordedTotal > 0
            ? recordedTotal
            : rows.reduce((sum, row) => sum + (Number(row?.discountAmount) || 0), 0);

    const bookingShare = taggedBookingAmount > 0 ? taggedBookingAmount : Number(bookingDiscountTotal) || 0;
    const settlementAmount = sumBySource(rows, 'SETTLEMENT');
    const regularAmount = Math.max(0, discountTotal - bookingShare - settlementAmount);

    return [
        { source: null as null, amount: regularAmount },
        { source: 'SETTLEMENT' as const, amount: settlementAmount },
    ].filter((discount) => discount.amount > 0.01);
};

/**
 * Splits a sale's discount total into the part its booking granted and the part added at
 * checkout to settle a deposit shortfall. Returns null when the sale carries no source-tagged
 * discounts (sales made before discountSource existed, or plain POS discounts), so callers can
 * keep showing a single combined total.
 */
export const getSaleDiscountBreakdown = (salesData: SaleWithDiscounts | null | undefined) => {
    if (!salesData) return null;

    const rows = getSaleDiscountRows(salesData);
    const bookingAmount = sumBySource(rows, 'BOOKING');
    const settlementAmount = sumBySource(rows, 'SETTLEMENT');
    if (bookingAmount <= 0 && settlementAmount <= 0) return null;

    // Any untagged discount on the same sale would make these two rows understate the total,
    // so fall back to the combined figure rather than showing a misleading split.
    const totalDiscount = Number(salesData.totalDiscount) || 0;
    if (totalDiscount > 0 && Math.abs(totalDiscount - (bookingAmount + settlementAmount)) > 0.01) {
        return null;
    }

    return {
        bookingAmount,
        settlementAmount,
        bookingName: rows.find((row) => row?.discountSource === 'BOOKING')?.discountName || null,
    };
};

export const getDiscountedPrice = (item: PostApiSaleBodyDataItemsItem): number => {
    // Always work with absolute price for discount calculations
    const basePrice = Math.abs(item.price || 0);
    const discountAmount = item.discountAmount ?? 0;

    if (item.discountType === 'VARIABLE_PERCENTAGE') {
        const discountValue = (basePrice * discountAmount) / 100;
        const finalPrice = basePrice - discountValue;
        return finalPrice;
    }

    return basePrice - discountAmount;
};
